import { ActionIcon, Box, Group, Paper, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconMicrophone,
  IconPlayerStop,
  IconX,
} from "@tabler/icons-react";
import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useWorkspaceMeetingNavigator from "hooks/useWorkspaceMeetingNavigator";
import { useFloatingMicAssistant } from "./FloatingMicAssistantContext";
import AssistantDock from "./dock/AssistantDock";
import MicChatDrawer from "./MicChatDrawer";
import useAssistantChat from "./useAssistantChat";
import useMicRecording from "./useMicRecording";
import { WAVEFORM_HEIGHT } from "./useLiveMicVisualizer";

const PRIMARY_BLUE = "#39a8f0";
const STOP_RED = "#e03131";
const AUTH_HIDDEN_PATHS = new Set([
  "/login",
  "/invite",
  "/onboarding-calendar",
  "/subscription",
  "/waitlist",
]);

const FloatingMicAssistant = () => {
  const location = useLocation();
  const {
    isChatOpen,
    openChat,
    closeChat,
    isVoiceModeOpen,
    openVoiceMode,
    closeVoiceMode,
  } = useFloatingMicAssistant();
  const {
    messages,
    isAiThinking,
    assistantProgress,
    sendMessage,
  } = useAssistantChat();
  const selectWorkspaceMeeting = useWorkspaceMeetingNavigator();

  const showMicError = useCallback((message) => {
    showNotification({
      title: "Voice input unavailable",
      message,
      color: "red",
    });
  }, []);

  const handleTranscript = useCallback(
    (transcript) => {
      openChat();
      sendMessage(transcript);
    },
    [openChat, sendMessage],
  );

  const {
    micState,
    startListening,
    stopListening,
    cancelListening,
    waveformCanvasRef,
    micOrbRef,
  } = useMicRecording({
    onTranscript: handleTranscript,
    onError: showMicError,
  });

  const isListening = micState === "listening";
  const isTranscribing = micState === "transcribing";
  const Icon = isListening ? IconPlayerStop : IconMicrophone;
  const bubbleColor = isListening ? STOP_RED : PRIMARY_BLUE;
  const bubbleHoverColor = isListening ? "#c92a2a" : "#228ed7";
  const isHiddenRoute =
    AUTH_HIDDEN_PATHS.has(location.pathname) ||
    location.pathname.startsWith("/invite/");

  useEffect(() => {
    if (isChatOpen || isVoiceModeOpen) {
      cancelListening();
    }
  }, [isChatOpen, isVoiceModeOpen, cancelListening]);

  const handleSendMessage = (content) => {
    sendMessage(content);
  };

  useEffect(() => {
    if (!isListening) return undefined;

    const handleListeningShortcut = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        cancelListening();
        return;
      }

      if (event.code !== "Space" && event.key !== " ") return;
      if (event.repeat) return;
      event.preventDefault();
      stopListening({ requireMinimumDuration: true });
    };

    document.addEventListener("keydown", handleListeningShortcut);
    return () => {
      document.removeEventListener("keydown", handleListeningShortcut);
    };
  }, [cancelListening, isListening, stopListening]);

  const handleBubbleClick = () => {
    if (isTranscribing) return;

    if (isListening) {
      stopListening();
      return;
    }

    startListening();
  };

  if (isHiddenRoute) return null;

  return (
    <>
      <MicChatDrawer
        opened={isChatOpen}
        onClose={closeChat}
        messages={messages}
        isAiThinking={isAiThinking}
        assistantProgress={assistantProgress}
        onSendMessage={handleSendMessage}
        onSelectSource={selectWorkspaceMeeting}
      />

      {/* Always mounted, never portalled: unmounting would tear down a live
          session and rebuild the orb's WebGL context on every open. It hides
          itself with CSS when another surface takes over. */}
      <AssistantDock
        opened={isVoiceModeOpen}
        hidden={isChatOpen || isListening || isTranscribing}
        onOpen={openVoiceMode}
        onClose={closeVoiceMode}
        onOpenChat={openChat}
        sendMessage={sendMessage}
      />

      {!isChatOpen && !isVoiceModeOpen && (
        <>
          <Box
            sx={(theme) => ({
              position: "fixed",
              right: theme.spacing.xl,
              bottom: theme.spacing.xl,
              zIndex: 1000,
            })}
          >
          {(isListening || isTranscribing) && (
            <Paper
              radius={24}
              shadow="xl"
              p="lg"
              sx={(theme) => ({
                position: "absolute",
                right: 0,
                bottom: 72,
                width: 320,
                border: "1px solid rgba(57, 168, 240, 0.18)",
                background: "rgba(255, 255, 255, 0.96)",
                backdropFilter: "blur(16px)",
                [theme.fn.smallerThan("xs")]: {
                  position: "fixed",
                  left: theme.spacing.md,
                  right: theme.spacing.md,
                  bottom: 88,
                  width: "auto",
                },
              })}
            >
              <Group position="apart" align="flex-start" noWrap>
                <Box>
                  <Text weight={700} size="lg" color="#373B54">
                    {isTranscribing
                      ? "Transcribing..."
                      : "Listening..."}
                  </Text>
                  <Text size="sm" color="dimmed" mt={4}>
                    {isTranscribing
                      ? "Turning your recording into a message."
                      : "Speak now. Click stop when you are done."}
                  </Text>
                  {isListening && (
                    <Group spacing={8} mt="sm">
                      <Text
                        component="span"
                        size="xs"
                        color="dimmed"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          lineHeight: 1,
                        }}
                      >
                        <Box
                          component="kbd"
                          sx={{
                            minWidth: 46,
                            padding: "5px 8px",
                            borderRadius: 6,
                            border: "1px solid #d9e8f4",
                            background: "#f5fbff",
                            color: "#2f6f9e",
                            fontFamily: "inherit",
                            fontSize: 11,
                            fontWeight: 700,
                            textAlign: "center",
                          }}
                        >
                          Space
                        </Box>
                        submit
                      </Text>
                      <Text
                        component="span"
                        size="xs"
                        color="dimmed"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          lineHeight: 1,
                        }}
                      >
                        <Box
                          component="kbd"
                          sx={{
                            minWidth: 30,
                            padding: "5px 8px",
                            borderRadius: 6,
                            border: "1px solid #e1e5eb",
                            background: "#f8fafc",
                            color: "#697386",
                            fontFamily: "inherit",
                            fontSize: 11,
                            fontWeight: 700,
                            textAlign: "center",
                          }}
                        >
                          Esc
                        </Box>
                        cancel
                      </Text>
                    </Group>
                  )}
                </Box>
                <ActionIcon
                  aria-label="Cancel listening"
                  radius="xl"
                  variant="subtle"
                  onClick={cancelListening}
                  disabled={isTranscribing}
                >
                  <IconX size={18} />
                </ActionIcon>
              </Group>

              {(isListening || isTranscribing) && (
                <Group position="center" mt="xl" mb="lg">
                  <Box
                    sx={{
                      position: "relative",
                      width: 136,
                      height: 136,
                      display: "grid",
                      placeItems: "center",
                      "&::before, &::after": {
                        content: '""',
                        position: "absolute",
                        inset: 6,
                        borderRadius: "50%",
                        background:
                          "radial-gradient(circle, rgba(57, 168, 240, 0.2) 0%, rgba(57, 168, 240, 0.04) 62%, transparent 72%)",
                        transform: isListening ? "scale(1)" : "scale(0.9)",
                        transition: "transform 220ms ease, opacity 220ms ease",
                      },
                      "&::after": {
                        inset: 0,
                        border: "1px solid rgba(57, 168, 240, 0.16)",
                        background: "transparent",
                      },
                    }}
                  >
                    <Box
                      ref={micOrbRef}
                      sx={{
                        position: "relative",
                        width: 74,
                        height: 74,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: "white",
                        background:
                          "linear-gradient(145deg, #53bdf7 0%, #228ed7 100%)",
                        boxShadow: "0 18px 36px rgba(57, 168, 240, 0.32)",
                        transition: isListening
                          ? "none"
                          : "transform 180ms ease, box-shadow 180ms ease",
                        zIndex: 1,
                      }}
                    >
                      <IconMicrophone size={34} stroke={2} />
                    </Box>
                  </Box>
                </Group>
              )}

              {isListening && (
                <Box
                  sx={{
                    position: "relative",
                    height: WAVEFORM_HEIGHT,
                    width: "100%",
                    marginTop: -8,
                    overflow: "hidden",
                  }}
                >
                  <canvas
                    ref={waveformCanvasRef}
                    aria-hidden="true"
                    style={{
                      display: "block",
                      width: "100%",
                      height: WAVEFORM_HEIGHT,
                    }}
                  />
                </Box>
              )}
            </Paper>
          )}

          <ActionIcon
            aria-label={isListening ? "Stop recording" : "Open microphone"}
            radius="xl"
            size={56}
            variant="filled"
            onClick={handleBubbleClick}
            disabled={isTranscribing}
            sx={{
              background: bubbleColor,
              boxShadow: isListening
                ? "0 14px 34px rgba(224, 49, 49, 0.32)"
                : "0 14px 34px rgba(57, 168, 240, 0.34)",
              "&:hover": {
                background: bubbleHoverColor,
              },
            }}
          >
            <Icon size={28} stroke={2} />
          </ActionIcon>
          </Box>
        </>
      )}
    </>
  );
};

export default FloatingMicAssistant;
